import type { CaregiverRole } from '@/types';

/**
 * Presentation label for a household access level. The DB enum values
 * (`PRIMARY`/`CO_ADMIN`/`EDITOR`/`VIEWER`) never surface in the UI —
 * this is the single place the vocabulary split (permission vs. the DB
 * value) lives. Unknown/absent → "Can view" (the least-privileged label).
 */
export function getAccessLevelLabel(role: string | null): string {
  switch (role) {
    case 'PRIMARY':
      return 'Admin';
    case 'CO_ADMIN':
      return 'Co-Admin';
    case 'EDITOR':
      return 'Can edit';
    default:
      return 'Can view';
  }
}

/** The access levels an invite can grant — every level except ownership. */
export type InvitableAccessLevel = Exclude<CaregiverRole, 'PRIMARY'>;

export interface AccessLevelOption {
  /** Form-facing value (kept distinct from the DB role for the picker). */
  value: string;
  /** The `caregiver_role` this option grants (never PRIMARY). */
  role: InvitableAccessLevel;
  label: string;
  description: string;
}

/**
 * Access levels offered when inviting a member, most-privileged first.
 * `PRIMARY` (ownership) is intentionally absent: a household has exactly
 * one owner and ownership moves only via transfer, never an invite. Both
 * Admin and Co-Admin may grant any of these (a Co-Admin can name another
 * Co-Admin but can never grant ownership — enforced in RLS + the edge
 * function, this list just mirrors it).
 *
 * The `description` strings are VERBATIM Figma (the Access level dropdown on
 * both the onboarding invite and Settings → Add member). They shipped as
 * longer, self-written sentences, which QA filed as "description of the
 * access level options doesn't match figma" (YAR-159). They are terse and
 * unpunctuated on purpose — the frame renders each one inline beside its
 * label, not as a paragraph beneath it.
 */
const ALL_INVITE_ACCESS_OPTIONS: AccessLevelOption[] = [
  {
    value: 'co_admin',
    role: 'CO_ADMIN',
    label: 'Co-Admin',
    description: 'Help manage this household',
  },
  {
    value: 'edit',
    role: 'EDITOR',
    label: 'Can edit',
    description: 'Edit tasks and events',
  },
  {
    value: 'view',
    role: 'VIEWER',
    label: 'Can view',
    description: 'View tasks and events',
  },
];

/**
 * Whether Co-Admin may be OFFERED in a picker.
 *
 * Defaults to hidden, and staging opts in with `VITE_ENABLE_CO_ADMIN_ROLE=true`.
 * The default is deliberately the restrictive one: the role's permission
 * boundaries are not settled yet, and the two ways this can be misconfigured
 * are not equally bad. Forgetting the flag in staging makes the option
 * disappear — visible immediately, harmless. Forgetting it in production would
 * grant a role we are not confident in, to a real household, silently.
 *
 * This is a DISPLAY gate only. The role still exists in the DB enum, RLS still
 * enforces it, and members who already hold it keep it and keep rendering as
 * "Co-Admin" via `getAccessLevelLabel`. Hiding the option must never look like
 * a demotion to someone who already has the role.
 */
export function isCoAdminRoleEnabled(): boolean {
  return import.meta.env.VITE_ENABLE_CO_ADMIN_ROLE === 'true';
}

/**
 * Access levels offered when inviting or editing a member, most-privileged
 * first — with Co-Admin filtered out unless the environment enables it.
 *
 * A function, not a constant: `import.meta.env` is inlined at build time, but
 * reading it per call keeps this testable without module-cache gymnastics.
 */
export function getInviteAccessOptions(): AccessLevelOption[] {
  return isCoAdminRoleEnabled()
    ? ALL_INVITE_ACCESS_OPTIONS
    : ALL_INVITE_ACCESS_OPTIONS.filter((o) => o.role !== 'CO_ADMIN');
}

/**
 * Every invitable level regardless of the flag. Use ONLY for mapping an
 * existing value/role back to its option — never to build a picker. A member
 * who already holds Co-Admin must still resolve to their real label, or the
 * UI would silently present them as an Editor.
 */
export const INVITE_ACCESS_OPTIONS: AccessLevelOption[] =
  ALL_INVITE_ACCESS_OPTIONS;

/**
 * The subset offered in the ONBOARDING invite step: "Can edit" + "Can view".
 * Per the Jul 12 access-levels one-pager the MVP invite dropdown ships exactly
 * these two — Co-Admin is defined in the DB but stays BACKEND-ONLY (no UI) at
 * MVP, and ownership (Admin) moves only via transfer, never an invite. Settings
 * keeps the fuller INVITE_ACCESS_OPTIONS (incl. Co-Admin) per the Jul 20 call.
 */
export const ONBOARDING_INVITE_ACCESS_OPTIONS: AccessLevelOption[] =
  ALL_INVITE_ACCESS_OPTIONS.filter((o) => o.role !== 'CO_ADMIN');

/** Maps an INVITE_ACCESS_OPTIONS `value` to the access level it grants. */
export function inviteValueToRole(value: string): InvitableAccessLevel {
  return INVITE_ACCESS_OPTIONS.find((o) => o.value === value)?.role ?? 'VIEWER';
}

/**
 * Maps a member's current `caregiver_role` back to the INVITE_ACCESS_OPTIONS
 * `value` (to pre-select it when editing an existing member's access). PRIMARY
 * has no invitable value — the caller should never open the editor for an
 * owner — so it falls back to the least-privileged option.
 */
export function roleToInviteValue(role: string | null): string {
  return INVITE_ACCESS_OPTIONS.find((o) => o.role === role)?.value ?? 'view';
}
